import type { MouseEvent, JSX } from 'react';
import { useRef } from 'react';
import * as yup from 'yup';
import { styled } from 'styled-components';
import { Form, FormField, FormButtons } from '@wistia/vhs';
import { trackStructEvent } from '~/utilities/snowplow';
import { noOpFn } from '~/utilities/util';
import { useCommentContext, commentingViews } from '../CommentContext';
import { BodyFieldWithContactSelect } from './BodyFieldWithContactSelect';

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & fieldset {
    margin: 0;
  }
`;

const StyledInput = styled(FormField)`
  /* stylelint-disable-next-line plugin/use-baseline */
  field-sizing: content;
`;

const formSchema = yup.object().shape({
  body: yup.string().trim().required(),
});

const BodyFormField = ({ fieldRef }: { fieldRef: React.RefObject<HTMLInputElement | null> }) => {
  const { commentingView } = useCommentContext();
  if (commentingView === commentingViews.audience) {
    return (
      <StyledInput
        name="body"
        ref={fieldRef}
        type="multiline"
      />
    );
  }
  return <BodyFieldWithContactSelect fieldRef={fieldRef} />;
};

export const UpdateCommentForm = ({
  commentId,
  initialBody,
  setShowEditForm,
}: {
  commentId: string;
  initialBody: string;
  setShowEditForm: (show: boolean) => void;
}): JSX.Element => {
  const formGlobalErrorRef = useRef(undefined);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const { updateComment, commentingView } = useCommentContext();

  const onSubmitForm = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const body = inputFieldRef.current?.value ?? initialBody;

    updateComment(body, commentId);
    setShowEditForm(false);

    trackStructEvent({
      category: 'button',
      action: 'click',
      label: `edit-media-page-${commentingView.toLowerCase()}-comment`,
    });
  };

  return (
    <StyledForm
      formId="updateCommentForm"
      globalErrorRef={formGlobalErrorRef}
      initialValues={{
        body: initialBody,
      }}
      onSubmit={noOpFn}
      style={{ marginTop: '20px' }}
      validationSchema={formSchema}
    >
      <BodyFormField fieldRef={inputFieldRef} />
      <FormButtons
        align="left"
        buttons={[
          {
            variant: 'secondary',
            type: 'button',
            size: 'md',
            name: 'Cancel',
            onClick: () => {
              setShowEditForm(false);
            },
          },
          {
            type: 'button',
            size: 'md',
            name: 'Save',
            variant: 'primary',
            onClick: onSubmitForm,
          },
        ]}
      />
    </StyledForm>
  );
};
